import { RESULT_META } from '@/pages/game/live-events/model/vocab.js';

// Intel's task chat runs only this task with the person's replies; nothing changes until someone applies its card.
export const TASK_CHAT_PAGE = 'live_event_task';

export const RUN_TASK_CARD = 'live_event_run_task';

export const taskContext = (instanceId, taskId) => `live-event-task;instanceId=${instanceId};taskId=${taskId}`;

export const runTaskCard = (step) => {
    const raw = step?.details?.output;
    if (step?.status !== 'completed' || typeof raw !== 'string') return null;
    try {
        const env = JSON.parse(raw);
        return env?.ok && env.action === 'update' && env.category === RUN_TASK_CARD && env.api_record?.result ? env : null;
    } catch {
        return null;
    }
};

// Each reply carries its question word for word, so the chat can record it against the right one.
export const repliesMessage = (answers) => answers.map((a) => `${a.question}\nMy answer: ${a.answer}`).join('\n\n');

const fmtNumber = (n) => (typeof n === 'number' ? String(Math.round(n * 1000) / 1000) : JSON.stringify(n));

const describeChange = (change, segmentName) => {
    const params = change.params || {};
    if (change.type === 'config.set_value') {
        const key = change.target?.key || 'A value';
        const op = String(params.op || 'SET').toUpperCase();
        if (op === 'MULTIPLY') return `${key} × ${fmtNumber(params.value)}`;
        if (op === 'ADD') return `${key} ${params.value < 0 ? '−' : '+'} ${fmtNumber(Math.abs(params.value))}`;
        return `${key} → ${fmtNumber(params.value)}`;
    }
    if (change.type === 'event.audience') return `Audience: ${(params.segment_ids || []).map(segmentName).join(', ') || 'everyone'}`;
    if (change.type === 'event.window') return `Live for ${params.hours} hours`;
    return change.type || 'A change';
};

// What the card shows for a proposed result in intel's wire shape (snake_case).
export const proposalView = (result, segmentName = (id) => id) => {
    const outputs = result.outputs || [];
    const changes = outputs.filter((o) => o.kind === 'change');
    const kind = result.status === 'COMPLETED' && !changes.length ? 'ANSWERED' : result.status;
    const meta = RESULT_META[kind] || RESULT_META.NOT_ACTIONABLE;
    return {
        kind,
        label: meta.label,
        tone: { COMPLETED: 'success', ANSWERED: 'info' }[kind] || 'warning',
        summary: result.summary || '',
        rows: [
            ...changes.map((c) => describeChange(c, segmentName)),
            ...outputs.filter((o) => o.kind === 'answer' && o.body).map((o) => o.body),
            ...(result.questions || []).map((q) => `Still asks: ${q.text}`),
            ...(result.blocked_by || []).map((b) => b.reason).filter(Boolean).map((r) => `Waits on: ${r}`),
        ],
    };
};

const doneAt = (task) => Date.parse(task.result?.processedAt) || 0;

// Why a task's result may be out of date: something it was built on changed after it ran. Nothing re-runs on its own.
export const staleNote = (task, tasks) => {
    const result = task.result;
    if (task.dropped || task.actor === 'MANUAL' || !result?.dependsOn?.length) return null;
    for (const id of result.dependsOn) {
        const dep = tasks.find((t) => t.id === id);
        if (!dep) continue;
        if (dep.dropped) return `Built on “${dep.title}”, which is dropped for this date. Run again to refresh.`;
        if (dep.actor === 'MANUAL') {
            if (result.status === 'NEEDS_INPUT' && (dep.done || dep.skipReason)) {
                return `“${dep.title}” is ${dep.done ? 'done' : 'skipped'} now. Run again to refresh.`;
            }
        } else if (doneAt(dep) > doneAt(task)) {
            return `Built on an earlier result of “${dep.title}”. Run again to refresh.`;
        }
    }
    return null;
};
